import React, { useEffect, useState } from "react";
import { api, UNIT_META } from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/ui-parts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { DialogShell, Field, Input } from "@/components/form-helpers";

const ROLES = ["admin", "manager", "operator", "viewer"];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer", assigned_units: [] });

  const load = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };
  useEffect(() => { load(); }, []);

  const updateRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleUnit = async (u, unitKey, checked) => {
    const assigned = new Set(u.assigned_units || []);
    if (checked) assigned.add(unitKey); else assigned.delete(unitKey);
    try {
      await api.patch(`/users/${u.user_id}`, { assigned_units: Array.from(assigned) });
      toast.success("Access updated");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const toggleFormUnit = (k, checked) => {
    setForm((f) => {
      const set = new Set(f.assigned_units);
      if (checked) set.add(k); else set.delete(k);
      return { ...f, assigned_units: Array.from(set) };
    });
  };

  const invite = async () => {
    if (!form.email || !form.email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      await api.post("/users/invite", form);
      toast.success(`Invited ${form.email}. They'll get the assigned access when they sign in with Google.`);
      setOpen(false);
      setForm({ email: "", name: "", role: "viewer", assigned_units: [] });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to invite");
    }
  };

  const removeUser = async (uid, email) => {
    if (uid === me?.user_id) { toast.error("You cannot remove yourself"); return; }
    if (!window.confirm(`Remove ${email}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin"
        title="User Access Control"
        description="Invite team members by their Google email, then assign role + unit-level access. When they sign in with that Google account, they get exactly the access you set here."
        testid="users-header"
        actions={
          <Button data-testid="invite-user-btn" onClick={() => setOpen(true)} className="bg-amber-400 hover:bg-amber-300 text-black rounded-none">
            <Plus className="mr-2" size={14} weight="bold" /> Invite User
          </Button>
        }
      />

      <Panel title={`Users (${users.length})`}>
        {users.length === 0 ? (
          <EmptyState message="No users" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="label-caps">User</TableHead>
                <TableHead className="label-caps">Email</TableHead>
                <TableHead className="label-caps">Role</TableHead>
                {Object.keys(UNIT_META).map((k) => (
                  <TableHead key={k} className="label-caps text-center">{UNIT_META[k].tag}</TableHead>
                ))}
                <TableHead className="label-caps">Status</TableHead>
                <TableHead className="label-caps text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id} className="border-zinc-800" data-testid={`user-row-${u.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="h-8 w-8" />
                      ) : (
                        <div className="h-8 w-8 bg-zinc-800 grid place-items-center text-xs">
                          {u.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="font-semibold text-sm">{u.name}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-400 text-xs">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => updateRole(u.user_id, v)}>
                      <SelectTrigger data-testid={`role-select-${u.user_id}`} className="rounded-none bg-zinc-950 border-zinc-800 h-8 w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  {Object.keys(UNIT_META).map((k) => (
                    <TableCell key={k} className="text-center">
                      <Checkbox
                        data-testid={`unit-toggle-${u.user_id}-${k}`}
                        checked={(u.assigned_units || []).includes(k)}
                        onCheckedChange={(c) => toggleUnit(u, k, !!c)}
                        disabled={u.role === "admin"}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    {u.picture ? (
                      <span className="label-caps text-emerald-400 text-[9px]">Active</span>
                    ) : (
                      <span className="label-caps text-amber-400 text-[9px] inline-flex items-center gap-1">
                        <EnvelopeSimple size={11} weight="bold" /> Invited
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      data-testid={`remove-user-${u.user_id}`}
                      onClick={() => removeUser(u.user_id, u.email)}
                      disabled={u.user_id === me?.user_id}
                      className="text-red-400 hover:text-red-300 disabled:text-zinc-700 disabled:cursor-not-allowed"
                      aria-label="Remove user"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <div className="mt-4 p-4 border border-zinc-800 text-[11px] text-zinc-500 font-mono leading-relaxed">
        <div className="label-caps mb-2 text-amber-400">Access matrix</div>
        <div>ADMIN → full access to all units & user management</div>
        <div>MANAGER → read + write on assigned units</div>
        <div>OPERATOR → data entry only on assigned units</div>
        <div>VIEWER → read-only on assigned units</div>
      </div>

      <DialogShell
        open={open}
        onOpenChange={setOpen}
        title="Invite a team member"
        onSubmit={invite}
        submitLabel="Send Invite"
        testidSubmit="save-invite-btn"
      >
        <Field label="Google email">
          <Input
            data-testid="invite-email"
            type="email"
            className="rounded-none bg-zinc-950 border-zinc-800"
            placeholder="operator@yourcompany.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Display name (optional)">
          <Input
            data-testid="invite-name"
            className="rounded-none bg-zinc-950 border-zinc-800"
            placeholder="Ramesh Kumar"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Role">
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="invite-role" className="rounded-none bg-zinc-950 border-zinc-800">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Assigned units">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(UNIT_META).map(([k, meta]) => (
              <label key={k} className="flex items-center gap-2 border border-zinc-800 px-3 py-2 cursor-pointer hover:bg-zinc-900">
                <Checkbox
                  data-testid={`invite-unit-${k}`}
                  checked={form.assigned_units.includes(k)}
                  onCheckedChange={(c) => toggleFormUnit(k, !!c)}
                />
                <span className="text-xs">{meta.name}</span>
              </label>
            ))}
          </div>
        </Field>
        <div className="text-[11px] font-mono text-zinc-500 border-t border-zinc-800 pt-3">
          They&apos;ll sign in with Google using this exact email. Access is applied automatically.
        </div>
      </DialogShell>
    </div>
  );
}
